// =============================================================================
// MeuDriver — Google Calendar OAuth Callback Page (Batch V1)
// Recebe o redirect do Google com ?code=...&state=... e finaliza o OAuth flow.
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CalendarCheck, AlertCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

type PageState = 'loading' | 'error' | 'redirecting'

export default function GoogleCalendarCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const invokedRef = useRef(false)

  const [pageState, setPageState] = useState<PageState>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const code = searchParams.get('code')
  const state = searchParams.get('state')

  useEffect(() => {
    // Evita execução dupla no StrictMode
    if (invokedRef.current) return
    invokedRef.current = true

    // Parâmetros ausentes → erro imediato
    if (!code || !state) {
      setErrorMessage('Parâmetros inválidos na URL de callback.')
      setPageState('error')
      return
    }

    // Usuário não autenticado → redirecionar para login com return_to
    if (!user) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
      navigate(`/login?return_to=${returnTo}`, { replace: true })
      return
    }

    async function exchangeCode() {
      try {
        const { error } = await supabase.functions.invoke('google-calendar-auth', {
          method: 'POST',
          body: { code, state },
        } as Parameters<typeof supabase.functions.invoke>[1])

        if (error) {
          throw new Error(
            typeof error === 'object' && 'message' in error
              ? (error as { message: string }).message
              : 'Erro ao conectar o Google Calendar.',
          )
        }

        setPageState('redirecting')
        toast.success('Google Calendar conectado!')
        navigate('/aluno/perfil', { replace: true })
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Não foi possível conectar o Google Calendar. Tente novamente.'
        toast.error(message)
        setErrorMessage(message)
        setPageState('error')
      }
    }

    exchangeCode()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Tela de erro ─────────────────────────────────────────────────────────────

  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 flex flex-col items-center gap-5 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-gray-900">Falha na conexão</h2>
              <p className="text-sm text-muted-foreground max-w-xs">
                {errorMessage ?? 'Ocorreu um erro ao conectar o Google Calendar.'}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/aluno/perfil', { replace: true })}
              data-testid="gcal-callback-error-back"
            >
              Voltar ao perfil
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Tela de loading / redirecionando ─────────────────────────────────────────

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8 flex flex-col items-center gap-5 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-100">
            <CalendarCheck className="h-8 w-8 text-brand-600" />
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">Conectando ao Google Calendar...</h2>
            <p className="text-sm text-muted-foreground">Aguarde enquanto finalizamos a conexão.</p>
          </div>
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" data-testid="gcal-callback-spinner" />
        </CardContent>
      </Card>
    </div>
  )
}
